// Room data validator — the hard GATE for authored rooms (human- or agent-made).
// Pure (no render/navmesh deps) so it runs headless in CI and inside the multi-model
// verify loop. Schema shape is enforced by Zod (core/schema); this adds the geometry
// and semantic checks the schema can't express. See research/DECISIONS.md, Decision 4 + 6.
import { pointInAny, type Polygon, type Vec2 } from "../core/geometry";
import type { Room } from "../core/schema";

export interface ValidationResult {
  ok: boolean;
  errors: string[]; // hard failures — room must not load
  warnings: string[]; // suspicious but not fatal
}

const centroid = (poly: Polygon): Vec2 => {
  const s = poly.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
  return { x: s.x / poly.length, y: s.y / poly.length };
};

function signedArea(poly: Polygon): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += (poly[j]!.x + poly[i]!.x) * (poly[j]!.y - poly[i]!.y);
  }
  return a / 2;
}

/** Convex (and therefore simple) — navmesh requires convex polygons. */
function isConvex(poly: Polygon): boolean {
  let sign = 0;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    const c = poly[(i + 2) % n]!;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-9) continue;
    const s = Math.sign(cross);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

/** Do two segments share a collinear, overlapping stretch (a portal seam)? */
function segsShareEdge(a1: Vec2, a2: Vec2, b1: Vec2, b2: Vec2): boolean {
  const ux = a2.x - a1.x;
  const uy = a2.y - a1.y;
  const L2 = ux * ux + uy * uy;
  if (L2 < 1e-9) return false;
  const L = Math.sqrt(L2);
  const perp = (p: Vec2) => Math.abs((p.x - a1.x) * uy - (p.y - a1.y) * ux) / L;
  if (perp(b1) > 1e-6 || perp(b2) > 1e-6) return false; // not collinear
  const t = (p: Vec2) => ((p.x - a1.x) * ux + (p.y - a1.y) * uy) / L2;
  let tb0 = t(b1);
  let tb1 = t(b2);
  if (tb0 > tb1) [tb0, tb1] = [tb1, tb0];
  const lo = Math.max(0, tb0);
  const hi = Math.min(1, tb1);
  return (hi - lo) * L > 1; // overlap longer than 1px
}

function polysAdjacent(A: Polygon, B: Polygon): boolean {
  for (let i = 0, j = A.length - 1; i < A.length; j = i++) {
    for (let k = 0, l = B.length - 1; k < B.length; l = k++) {
      if (segsShareEdge(A[j]!, A[i]!, B[l]!, B[k]!)) return true;
    }
  }
  return false;
}

function allConnected(polys: Polygon[]): boolean {
  if (polys.length <= 1) return true;
  const seen = new Set<number>([0]);
  const stack = [0];
  while (stack.length) {
    const cur = stack.pop()!;
    for (let i = 0; i < polys.length; i++) {
      if (!seen.has(i) && polysAdjacent(polys[cur]!, polys[i]!)) {
        seen.add(i);
        stack.push(i);
      }
    }
  }
  return seen.size === polys.length;
}

export function validateRoom(room: Room): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const within = (p: Vec2) => p.x >= 0 && p.y >= 0 && p.x <= room.size.w && p.y <= room.size.h;

  room.walkmesh.forEach((poly, i) => {
    if (poly.length < 3) errors.push(`walkmesh[${i}] has fewer than 3 vertices`);
    if (Math.abs(signedArea(poly)) < 1) errors.push(`walkmesh[${i}] is degenerate (near-zero area)`);
    if (!isConvex(poly)) errors.push(`walkmesh[${i}] is not convex (navmesh requires convex polygons)`);
    poly.forEach((p) => {
      if (!within(p)) warnings.push(`walkmesh[${i}] vertex (${p.x},${p.y}) is outside room bounds`);
    });
  });

  if (!pointInAny(room.spawn, room.walkmesh as Polygon[])) {
    errors.push(`spawn (${room.spawn.x},${room.spawn.y}) is not on the walkmesh`);
  }
  if (room.walkmesh.length > 1 && !allConnected(room.walkmesh as Polygon[])) {
    warnings.push(`walkmesh has disconnected regions — the hero may not reach all areas`);
  }

  // gated areas must be valid convex polygons too (they join the navmesh)
  room.gatedAreas.forEach((a) => {
    if (a.polygon.length < 3) errors.push(`gatedArea '${a.id}' has fewer than 3 vertices`);
    else if (Math.abs(signedArea(a.polygon)) < 1) errors.push(`gatedArea '${a.id}' is degenerate (near-zero area)`);
    else if (!isConvex(a.polygon)) errors.push(`gatedArea '${a.id}' is not convex (navmesh requires convex polygons)`);
  });

  // a ROOM-scoped gate whose key is never set by initial state or any room effect can
  // never change — a typo trap. (Global-scoped gates are checked across rooms in validateWorld.)
  const settable = new Set<string>(Object.keys(room.state));
  for (const h of room.hotspots) {
    if (h.effect?.set) Object.keys(h.effect.set).forEach((k) => settable.add(k));
    if (h.effect?.toggle) h.effect.toggle.forEach((k) => settable.add(k));
  }
  const gateConds = [
    ...room.gatedAreas.map((a) => a.gatedBy),
    ...room.hotspots.flatMap((h) => (h.gatedBy ? [h.gatedBy] : [])),
    ...room.exits.flatMap((e) => (e.gatedBy ? [e.gatedBy] : [])),
  ];
  for (const c of gateConds) {
    if (c.scope === "global" || c.key === undefined) continue; // global: world-level; no key: flame-only
    if (!settable.has(c.key)) warnings.push(`gating key '${c.key}' is never set by state or any effect — the gate can't change`);
  }

  room.hotspots.forEach((h) => {
    if (h.polygon.length < 3) errors.push(`hotspot '${h.id}' polygon has fewer than 3 vertices`);
  });
  room.occluders.forEach((o) => {
    if (o.baselineY < 0 || o.baselineY > room.size.h) warnings.push(`occluder '${o.id}' baselineY is out of bounds`);
  });
  room.npcs.forEach((n) => {
    if (!pointInAny(n, room.walkmesh as Polygon[])) warnings.push(`npc '${n.id}' spawn (${n.x},${n.y}) is off the walkmesh`);
    (n.patrol ?? []).forEach((p, i) => {
      if (!pointInAny(p, room.walkmesh as Polygon[]))
        warnings.push(`npc '${n.id}' patrol point ${i} (${p.x},${p.y}) is off the walkmesh`);
    });
  });
  room.exits.forEach((e) => {
    if (e.polygon.length < 3) errors.push(`exit '${e.id}' polygon has fewer than 3 vertices`);
    if (!pointInAny(centroid(e.polygon), room.walkmesh as Polygon[])) {
      warnings.push(`exit '${e.id}' center is not on the walkmesh — the hero may not be able to reach it`);
    }
    if (e.transition === "edge-pan" && !e.pan) warnings.push(`exit '${e.id}' is edge-pan but has no pan vector`);
  });

  return { ok: errors.length === 0, errors, warnings };
}

/** Validate a whole world: each room, plus cross-room exit wiring. */
export function validateWorld(rooms: Record<string, Room>): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const ids = new Set(Object.keys(rooms));

  // global gate keys must be set by SOME room's global effect, or they can never change
  const globalSettable = new Set<string>();
  for (const room of Object.values(rooms)) {
    for (const h of room.hotspots) {
      if (h.effect?.setGlobal) Object.keys(h.effect.setGlobal).forEach((k) => globalSettable.add(k));
      if (h.effect?.toggleGlobal) h.effect.toggleGlobal.forEach((k) => globalSettable.add(k));
    }
  }

  for (const [id, room] of Object.entries(rooms)) {
    const r = validateRoom(room);
    r.errors.forEach((e) => errors.push(`[${id}] ${e}`));
    r.warnings.forEach((w) => warnings.push(`[${id}] ${w}`));
    for (const ex of room.exits) {
      if (!ids.has(ex.target)) {
        errors.push(`[${id}] exit '${ex.id}' targets unknown room '${ex.target}'`);
      } else if (!pointInAny(ex.spawn, rooms[ex.target]!.walkmesh as Polygon[])) {
        errors.push(`[${id}] exit '${ex.id}' spawn is not on target room '${ex.target}' walkmesh`);
      }
    }
    const gconds = [
      ...room.gatedAreas.map((a) => a.gatedBy),
      ...room.hotspots.flatMap((h) => (h.gatedBy ? [h.gatedBy] : [])),
      ...room.exits.flatMap((e) => (e.gatedBy ? [e.gatedBy] : [])),
    ];
    for (const c of gconds) {
      if (c.scope === "global" && c.key !== undefined && !globalSettable.has(c.key)) {
        warnings.push(`[${id}] global gating key '${c.key}' is never set by any global effect`);
      }
    }
  }
  return { ok: errors.length === 0, errors, warnings };
}
